import { apiClient } from './client';

// ── Tipos ─────────────────────────────────────────────────────────────────────
// F0.4: alineados con el backend real (aws-serverless/src/models/shipment.model.ts).
// Antes estos tipos no coincidían con lo que la API realmente devuelve — la
// página renderizaba campos (quantityKg, containerCode, brokerRef, etc.) que
// nunca llegaban del servidor.

export type ShipmentExportStatus =
  | 'DRAFT' | 'PACKING' | 'SEALED' | 'DEPARTED'
  | 'CUSTOMS_SUBMITTED' | 'CUSTOMS_CLEARED' | 'CUSTOMS_HOLD'
  | 'FDA_SUBMITTED' | 'FDA_CLEARED' | 'FDA_HOLD'
  | 'ARRIVED' | 'DELIVERED' | 'CANCELLED';

export type ExportEventType =
  | 'PACKED' | 'SEALED' | 'TEMPERATURE_LOG' | 'DEPARTED'
  | 'CUSTOMS_SUBMITTED' | 'CUSTOMS_CLEARED' | 'CUSTOMS_HOLD'
  | 'FDA_SUBMITTED' | 'FDA_CLEARED' | 'FDA_HOLD'
  | 'ARRIVED' | 'DELIVERED' | 'INCIDENT';

export type ExportDocType =
  | 'PACKING_LIST' | 'PESTICIDE_DECLARATION' | 'INVOICE'
  | 'PHYTOSANITARY' | 'COA';

export interface ExportShipment {
  shipmentId:          string;
  tenantId:            string;
  status:              ShipmentExportStatus;
  product?:            string;
  variety?:            string;
  estimatedKg?:        number;
  actualKg?:           number;
  boxCount?:           number;
  containerId?:        string;
  sealNumber?:         string;
  carrier?:            string;
  destinationCountry?: string;
  buyer?:              string;
  departurePort?:      string;
  arrivalPort?:        string;
  scheduledDeparture?: string;
  actualDeparture?:    string;
  scheduledArrival?:   string;
  actualArrival?:      string;
  certifications?:     string[];
  tempMinC?:           number;
  tempMaxC?:           number;
  tlcs?:               string[];
  recipientLocation?:  string;
  originLocation?:     string;
  shipDate?:           string;
  referenceDocType?:   string;
  referenceDocNumber?: string;
  departedAt?:         string;
  createdAt:           string;
  updatedAt:           string;
}

export interface ExportEvent {
  shipmentId:    string;
  eventId:       string;
  eventType:     ExportEventType;
  occurredAt:    string;
  temperatureC?: number;
  humidityPct?:  number;
  location?:     string;
  isAnomaly?:    boolean;
  notes?:        string;
  operator?:     string;
  source?:       string;
}

export interface ExportDoc {
  shipmentId:  string;
  docType:     ExportDocType;
  s3Key:       string;
  generatedAt: string;
  generatedBy: string;
  version:     number;
  url?:        string;
}

export interface ExportKpis {
  totalShipments:     number;
  activeShipments:    number;
  deliveredShipments: number;
  onHoldShipments:    number;
  avgTransitDays?:    number;
  tempAnomalies:      number;
  delayCount:         number;
  exportKgTotal:      number;
  harvestKgTotal:     number;
  exportPct?:         number;
}

// ── Queries ───────────────────────────────────────────────────────────────────

export const fetchExportShipments = async (
  status?: ShipmentExportStatus,
  limit = 30,
  ranchoId?: string | null,
): Promise<{ shipments: ExportShipment[]; count: number }> =>
  (await apiClient.get('/api/exports', { params: { status, limit, ...(ranchoId ? { ranchoId } : {}) } })).data;

export const fetchExportKpis = async (ranchoId?: string | null): Promise<ExportKpis> =>
  (await apiClient.get('/api/exports/kpis', { params: ranchoId ? { ranchoId } : undefined })).data;

export const fetchExportShipment = async (
  shipmentId: string,
): Promise<{ shipment: ExportShipment; events: ExportEvent[] }> =>
  (await apiClient.get(`/api/exports/${shipmentId}`)).data;

export const fetchExportDoc = async (
  shipmentId: string,
  docType: ExportDocType,
): Promise<ExportDoc & { url: string }> =>
  (await apiClient.get(`/api/exports/${shipmentId}/docs/${docType}`)).data;
